<template>
  <v-card class="my-1" :style="{ '--layout-primary': themePrimary, '--layout-primary-tint': themePrimaryTint }">
    <v-toolbar flat dense>
      <v-toolbar-title>Spendenbetrag</v-toolbar-title>
    </v-toolbar>
    <v-card-text>

      <!-- Spendenbetrag: Min / Start / Maximum -->
      <div class="section-label">
        <span>SPENDENBETRAG</span>
        <v-tooltip bottom max-width="280">
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" x-small>{{ mdiInformationOutline }}</v-icon>
          </template>
          <span>Mindestbetrag, voreingestellter Startbetrag und maximaler Betrag auf diesem Spendengerät.</span>
        </v-tooltip>
      </div>

      <div class="track-wrap">
        <div class="track"></div>
        <div class="track__fill" :style="{ width: startTrackPct + '%' }"></div>

        <!-- Positions here are fixed and illustrative, not drawn to scale from the actual
             values - real amounts range from 1 to several hundred euros, so a value-accurate
             layout would crowd markers together or make them jump around as you type. -->
        <div class="track-dot track-dot--outline" style="left: 0%;"></div>
        <div class="track-dot track-dot--filled" :style="{ left: startTrackPct + '%', background: themePrimary, boxShadow: '0 0 0 2px ' + themePrimary }"></div>
        <div class="track-dot track-dot--outline track-dot--dark" style="left: 100%;"></div>

        <!-- Mindestbetrag: fixed business rule, not editable -->
        <div class="marker marker--below marker--align-start" style="left: 0%;">
          <div class="marker__label">Mindestbetrag</div>
          <div class="marker__value">{{ initial.minValue }}{{ currencySymbol }}</div>
        </div>

        <!-- Startwert -->
        <div class="marker marker--above" :style="{ left: startTrackPct + '%' }">
          <div class="marker__editable">
            <input
              :id="'id-'+initial.key"
              class="marker__input"
              type="number"
              :value="initial.currentValue"
              @input="val => emitInitial(val.target.value)"
              :min="initial.minValue"
              :max="startUpperBound"
              autocomplete="off"
            >
            <span class="marker__suffix">{{ currencySymbol }}</span>
            <reset-icon class="marker__reset" :setting="initial" @clear="$emit('clear', initial.key)" @set-here="$emit('set-here', initial.key)" />
          </div>
          <div class="marker__caption" :style="{ color: themePrimary }">START</div>
        </div>

        <!-- Maximalbetrag -->
        <div class="marker marker--below marker--right" style="left: 100%;">
          <div class="marker__label">Maximalbetrag</div>
          <div class="marker__editable">
            <input
              :id="'id-'+max.key"
              class="marker__input"
              type="number"
              :value="max.currentValue"
              @input="val => emitClamped(max.key, val.target.value, max.minValue, max.maxValue)"
              :min="max.minValue"
              :max="max.maxValue"
              autocomplete="off"
            >
            <span class="marker__suffix">{{ currencySymbol }}</span>
            <reset-icon class="marker__reset" :setting="max" @clear="$emit('clear', max.key)" @set-here="$emit('set-here', max.key)" />
          </div>
        </div>
      </div>

      <!-- Anzahl der Sammlungszwecke (only on devices that support more than one) -->
      <template v-if="collectionCount">
        <div class="section-label section-label--spaced">
          <span>SAMMLUNGSZWECKE</span>
          <v-tooltip bottom max-width="280">
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" x-small>{{ mdiInformationOutline }}</v-icon>
            </template>
            <span>Anzahl der Spendenzwecke, zwischen denen auf diesem Spendengerät gewählt werden kann.</span>
          </v-tooltip>
          <reset-icon :setting="collectionCount" @clear="$emit('clear', collectionCount.key)" @set-here="$emit('set-here', collectionCount.key)" />
        </div>
        <div class="collection-row" role="radiogroup" aria-label="Anzahl der Sammlungszwecke">
          <button
            v-for="n in collectionCount.maxValue"
            :key="n"
            type="button"
            class="collection-choice"
            :class="{ 'collection-choice--selected': n <= collectionCount.currentValue }"
            :style="n <= collectionCount.currentValue ? { background: themePrimary, borderColor: themePrimary } : {}"
            @click="emitClamped(collectionCount.key, n, collectionCount.minValue, collectionCount.maxValue)"
          >{{ collectionLabels[n] || n }}</button>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { mdiInformationOutline } from '@mdi/js'
import ResetIcon from '@/components/device/ResetIcon'

export default {
  name: 'HardwareAmountGroup',
  components: { ResetIcon },
  props: {
    initial: {
      type: Object,
      required: true,
    },
    max: {
      type: Object,
      required: true,
    },
    collectionCount: {
      type: Object,
      default: null,
    },
    currency: {
      type: String,
      default: 'EUR',
    },
  },
  data () {
    return {
      mdiInformationOutline,
      // Fixed, illustrative position for the Startwert marker on the Spendenbetrag
      // track - see LayoutGroup.vue for why this isn't drawn to scale from the
      // actual amounts.
      startTrackPct: 30,
      collectionLabels: {
        1: 'Eine Sammlung',
        2: 'Zwei Sammlungen',
        3: 'Drei Sammlungen',
      },
    }
  },
  computed: {
    currencySymbol () {
      return this.$options.filters.currencySymbol(this.currency)
    },
    themePrimary () {
      return this.$vuetify.theme.currentTheme.primary
    },
    themePrimaryTint () {
      return this.hexToRgba(this.themePrimary, 0.08)
    },
    startUpperBound () {
      return this.max.currentValue
    },
  },
  methods: {
    hexToRgba (hex, alpha) {
      let h = String(hex || '').replace('#', '')
      if (h.length === 3) {
        h = h.split('').map(c => c + c).join('')
      }
      const r = parseInt(h.substring(0, 2), 16) || 0
      const g = parseInt(h.substring(2, 4), 16) || 0
      const b = parseInt(h.substring(4, 6), 16) || 0
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    emitInput (key, value) {
      this.$emit('input', { key, value })
    },
    emitClamped (key, rawValue, min, max) {
      let value = parseInt(rawValue)
      if (isNaN(value)) {
        return
      }
      value = Math.min(value, max)
      value = Math.max(value, min)
      this.emitInput(key, value)
    },
    emitInitial (rawValue) {
      this.emitClamped(this.initial.key, rawValue, this.initial.minValue, this.startUpperBound)
    },
  },
}
</script>

<style scoped>
.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, .6);
  letter-spacing: .03em;
  margin-bottom: 30px;
}
.section-label--spaced {
  margin-top: 28px;
  margin-bottom: 14px;
}

.track-wrap {
  position: relative;
  height: 160px;
  margin: 0 10px 8px;
}
.track, .track__fill {
  position: absolute;
  top: 80px;
  height: 8px;
  border-radius: 4px;
}
.track {
  left: 0;
  right: 0;
  background: #e4e4e4;
}
.track__fill {
  left: 0;
  background: var(--layout-primary);
  opacity: .25;
}
.track-dot {
  position: absolute;
  top: 80px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transform: translate(-50%, -50%);
}
.track-dot--outline {
  border: 3px solid rgba(0, 0, 0, .4);
}
.track-dot--dark {
  border-color: rgba(0, 0, 0, .7);
}
.track-dot--filled {
  border: 3px solid #fff;
}

.marker {
  position: absolute;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}
.marker--above {
  top: 0;
}
.marker--below {
  top: 100px;
}
.marker--align-start {
  transform: translateX(0);
  align-items: flex-start;
}
.marker--right {
  transform: translateX(-100%);
  align-items: flex-end;
}
.marker__label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, .6);
  white-space: nowrap;
}
.marker__value {
  font-size: 19px;
  font-weight: 700;
  color: rgba(0, 0, 0, .8);
}
.marker__caption {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .04em;
  margin-top: 6px;
}
.marker__editable {
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(0, 0, 0, .05);
  border: 1px solid rgba(0, 0, 0, .15);
  border-radius: 8px;
  padding: 3px 4px 3px 10px;
}
.marker__editable:hover {
  border-color: rgba(0, 0, 0, .3);
}
.marker__editable:focus-within {
  border-color: var(--layout-primary);
  background: #fff;
}
.marker__input {
  width: 4.5ch;
  border: none;
  text-align: right;
  font-size: 19px;
  font-weight: 700;
  color: rgba(0, 0, 0, .8);
  font-family: inherit;
  background: transparent;
  -moz-appearance: textfield;
  cursor: text;
}
.marker__input::-webkit-outer-spin-button,
.marker__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.marker__input:focus {
  outline: none;
}
.marker__suffix {
  font-size: 15px;
  color: rgba(0, 0, 0, .55);
}
.marker__reset {
  margin-top: 4px;
}

.collection-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.collection-choice {
  border-radius: 20px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, .6);
  border: 2px solid rgba(0, 0, 0, .15);
  background: transparent;
  cursor: pointer;
}
.collection-choice--selected {
  color: #fff;
}
</style>
